'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { FC, useState } from 'react';
import { CmsImage } from '../../../components/CmsImage';
import { FeLink } from '../../../components/FeLink';
import FacebookIcon from '../../../components/icons/FacebookIcon';
import InstagramIcon from '../../../components/icons/InstagramIcon';
import LinkedinIcon from '../../../components/icons/LinkedinIcon';
import { TikTokIcon } from '../../../components/icons/TikTokIcon';
import YoutubeIcon from '../../../components/icons/YoutubeIcon';
import { BRAND_NAMES } from '../../../modules/segment';
import { CmsSettings } from '../../../types/cmsSettings';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getHomePagePath } from '../../../utils/routing';
import { CmsNavigation } from '../types';
import { getLogoImageSizing } from '../utils/logoImage';
import { navbarLinkToUrl } from '../utils/utils';

export interface TheFooterProps {
  navigation: CmsNavigation;
  settings: CmsSettings;
  version?: string;
  pushDataLayer: PushDataLayerFn;
}

const TheFooter: FC<TheFooterProps> = (props) => {
  const { brand, langPrefix } = useCmsClientContext();
  const { navigation, settings, version, pushDataLayer } = props;

  const { items: footer_menu } = navigation;

  const {
    disclaimer,
    company_line,
    site_socials,
    site_footer_logo: { url: appFooterLogo, alternativeText: appFooterLogoAlt },
  } = settings;

  const [openLinks, setOpenLinks] = useState<string[]>([]);

  const toggleLink = (key: string) => {
    if (openLinks.includes(key)) {
      if (openLinks.length === 1) {
        setOpenLinks(openLinks.filter((item) => item !== key));
      } else {
        setOpenLinks([]);
      }
    } else {
      if (openLinks.length > 0) {
        setOpenLinks([key]);
      } else {
        setOpenLinks((prev) => [...prev, key]);
      }
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer
      data-component="the-footer"
      className="relative z-10 bg-brand-primary-dark"
    >
      <div className="mx-auto max-w-[1536px] px-4 py-8 md:py-11 lg:px-8 lg:py-16 xl:py-14">
        <div className="flex flex-col border-b border-primary-800 pb-6 lg:flex-row">
          <div className="mb-0 basis-3/12 border-b border-primary-800 lg:border-0 xl:mb-0">
            <FeLink
              href={getHomePagePath(langPrefix)}
              onClick={() =>
                pushDataLayer({
                  event: 'navigate',
                  component_type: 'footer',
                  component_title: 'Footer_Logo',
                  action: 'click',
                  index: '0.0',
                })
              }
            >
              <CmsImage
                src={appFooterLogo}
                alt={appFooterLogoAlt || BRAND_NAMES[brand]}
                {...getLogoImageSizing(settings.site_footer_logo)}
                className="mb-6 h-6 w-auto md:h-8 xl:mb-0"
              />
            </FeLink>
          </div>
          {footer_menu && (
            <div className="flex basis-9/12 flex-wrap gap-5 pt-6 md:gap-20 lg:flex-nowrap lg:gap-10 lg:pt-0">
              {footer_menu.map((section, sectionIndex) => {
                const sectionUrl = navbarLinkToUrl(section!, {
                  brand,
                  langPrefix,
                });
                return (
                  <div
                    key={`${sectionUrl.text}.${sectionUrl.href}`}
                    className="basis-5/12 md:basis-2/12 lg:flex-1 lg:basis-5/12"
                  >
                    <p className="text-white alpha-body-1 md:text-[18px]">
                      {section!.label}
                    </p>
                    <ul className="mt-3 space-y-1">
                      {section!.sublinks!.map((link, linkIndex) => {
                        const key = `${sectionIndex}.${linkIndex}`;
                        const linkUrl = navbarLinkToUrl(link!, {
                          brand,
                          langPrefix,
                        });
                        return (
                          <li
                            key={key}
                            className="flex flex-col gap-2 transition-all duration-100"
                          >
                            <div className="flex flex-row items-center gap-1">
                              {link!.sublinks && link!.sublinks.length > 0 && (
                                <svg
                                  className={
                                    openLinks.includes(key) ? 'rotate-90' : ''
                                  }
                                  width="6"
                                  height="8"
                                  viewBox="0 0 6 8"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3.63112 4.00017L0.564453 0.933507L1.267 0.230957L5.03622 4.00017L1.267 7.76939L0.564453 7.06684L3.63112 4.00017Z"
                                    fill="#B7E7F2"
                                  />
                                </svg>
                              )}
                              {!link!.sublinks ||
                              link!.sublinks.length === 0 ? (
                                <FeLink
                                  href={linkUrl.href || '#'}
                                  target={
                                    linkUrl.url_target === '_blank'
                                      ? '_blank'
                                      : '_self'
                                  }
                                  className="text-primary-100 text-sm"
                                  onClick={() =>
                                    pushDataLayer({
                                      event: 'navigate',
                                      component_type: 'footer',
                                      component_title: `Footer_${sectionUrl.text}-${linkUrl.text}`,
                                      action: 'click',
                                      index: key,
                                    })
                                  }
                                  rel="noreferrer"
                                >
                                  {linkUrl.text}
                                </FeLink>
                              ) : (
                                <button
                                  type="button"
                                  className="text-left text-primary-100 text-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLink(key);
                                  }}
                                >
                                  {linkUrl.text}
                                </button>
                              )}
                            </div>
                            {link!.sublinks && openLinks.includes(key) && (
                              <ul className="flex flex-col gap-1 pl-2.5">
                                {link!.sublinks.map((sublink, subIndex) => {
                                  const sublinkUrl = navbarLinkToUrl(sublink!, {
                                    brand,
                                    langPrefix,
                                  });
                                  return (
                                    <li
                                      key={`${key}.${sublinkUrl.text}.${sublinkUrl.href}`}
                                    >
                                      <FeLink
                                        href={sublinkUrl.href || '#'}
                                        target={
                                          sublinkUrl.url_target === '_blank'
                                            ? '_blank'
                                            : '_self'
                                        }
                                        className="text-left text-primary-100 text-sm"
                                        onClick={() =>
                                          pushDataLayer({
                                            event: 'navigate',
                                            component_type: 'footer',
                                            component_title: `Footer_${sectionUrl.text}-${linkUrl.text}+${sublinkUrl.text}`,
                                            action: 'click',
                                            index: `${key}.${subIndex}`,
                                          })
                                        }
                                        rel="noreferrer"
                                      >
                                        {sublinkUrl.text}
                                      </FeLink>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="my-8 flex flex-col-reverse justify-between gap-4 lg:flex-row lg:items-center">
          <p className="text-primary-100 text-[12px] md:text-[14px] lg:text-[16px]">
            © 2015 - {currentYear}{' '}
            {company_line || 'SingSaver PTE LTD. All rights reserved'}
          </p>
          <div className="flex items-center gap-4">
            {site_socials &&
              site_socials.map((social) => (
                <FeLink
                  key={`${social?.name}.${social?.url}`}
                  href={social!.url}
                  target="_blank"
                  onClick={() =>
                    pushDataLayer({
                      event: 'navigate',
                      component_type: 'footer',
                      component_title: `Social Media Link_${social!.name}`,
                      action: 'click',
                      index: '0',
                    })
                  }
                  rel="noreferrer"
                >
                  {social!.name === 'facebook' ? (
                    <FacebookIcon />
                  ) : social!.name === 'instagram' ? (
                    <InstagramIcon />
                  ) : social!.name === 'youtube' ? (
                    <YoutubeIcon />
                  ) : social!.name === 'linkedin' ? (
                    <LinkedinIcon />
                  ) : social!.name === 'tiktok' ? (
                    <TikTokIcon />
                  ) : (
                    '#'
                  )}
                </FeLink>
              ))}
          </div>
        </div>
        {disclaimer ? (
          <div
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className="text-primary-100 text-[12px] md:text-[14px] lg:text-[16px]"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: disclaimer }}
          />
        ) : (
          <div
            // eslint-disable-next-line tailwindcss/no-custom-classname
            className="text-primary-100 text-[12px] md:text-[14px] lg:text-[16px]"
          >
            This online platform is operated by SingSaver Pte. Ltd. and by
            continuing to use www.singsaver.com.sg (the “SingSaver Website”) or
            by carrying out a transaction on the SingSaver Website, users are
            deemed to have agreed with the{' '}
            <FeLink
              href="https://www.singsaver.com.sg/page/singsaver-terms"
              target="_blank"
              rel="noreferrer"
            >
              Terms and Conditions
            </FeLink>{' '}
            and{' '}
            <FeLink
              href="https://www.singsaver.com.sg/page/singsaver-privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </FeLink>{' '}
            of the SingSaver Website. All insurance product-related transactions
            on the SingSaver Website are arranged and administered by SingSaver
            Insurance Brokers Pte. Ltd., a wholly owned subsidiary of SingSaver
            Pte. Ltd. SingSaver Insurance Brokers Pte. Ltd. is a licensed and
            authorised broker regulated by the Monetary Authority of Singapore
            (“MAS”). SingSaver Pte. Ltd. (which administers all other financial
            products on the SingSaver Website) is not regulated by MAS.
            SingSaver is located at Eon Shenton, 70 Shenton Way, 18-08,
            Singapore, 079118.
          </div>
        )}
        <span className="absolute bottom-0 left-0 text-brand-primary-dark">
          {`fe ${version || 'unknown'}`}
        </span>
      </div>
    </footer>
  );
};

export { TheFooter };
